import React, { useEffect, useRef, useState } from 'react';
import { createAssignSharedRefs } from 'utilities/input.helpers';
import { Button } from 'components/Button/Button';
import { CheckCircleIcon, ExclamationOctagonIcon } from 'components/Icon';
import { Tag } from 'components/Tag/Tag';
import { Typography } from 'components/Typography/Typography';
import { validateFiles } from './utils';
import * as S from './FileInput.styles';
const LOADING_THRESHOLD = 2000;
const errorMessages = {
  'file-too-large': 'The file is too large and could not be uploaded.',
  'file-too-small': 'The file is too small and could not be uploaded.',
  'file-invalid-type': 'Invalid file type.',
  'too-many-files': 'Too many files uploaded.',
  'more-than-one-file': 'Too many files uploaded. Please limit your upload to one file.'
};
const FileInput = /*#__PURE__*/React.forwardRef(({
  accept,
  infoMessage,
  id,
  onUpload,
  onDismiss,
  onError,
  value,
  onValueChange,
  errorMessage,
  hasError,
  alignment = 'left',
  multiple = true,
  maxFiles = 10,
  maxSize = 1e7,
  minSize = 0,
  initialValue = []
}, ref) => {
  const [startLoadingCounter, setStartLoadingCounter] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [error, setError] = useState();
  const [currentFilename, setCurrentFilename] = useState('');
  const [internalValue, setInternalValue] = useState(() => initialValue);
  const uploadedFiles = value ?? internalValue;
  if (value && !onValueChange) {
    console.error('When using `value` one must declare `onValueChange` to keep the component fully controlled');
  }
  const internalInputRef = useRef(null);
  const assignSharedRefs = createAssignSharedRefs(ref, internalInputRef);

  // Establishing a threshold before setting `loading` state in order to avoid flickering.
  useEffect(() => {
    let timeoutRef = null;
    if (!isLoading) {
      if (startLoadingCounter) {
        timeoutRef = setTimeout(() => {
          setIsLoading(true);
        }, LOADING_THRESHOLD);
      }
      if (startLoadingCounter) {
        return () => {
          if (timeoutRef) {
            clearTimeout(timeoutRef);
          }
        };
      }
    }
    return () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
        setIsLoading(false);
      }
    };
  }, [startLoadingCounter, isLoading]);
  const processFiles = files => {
    const {
      code,
      filename
    } = validateFiles({
      files,
      multiple,
      maxFiles,
      accept,
      maxSize,
      minSize
    });
    if (code !== 'no-errors') {
      const error = {
        code: code,
        message: errorMessages[code]
      };
      setError(error);
      if (onError) {
        onError(error);
      }
      if (filename) {
        setCurrentFilename(filename);
      }
      return;
    }

    // We only proccess the files if there are no errors in any of them
    setError(null);
    for (let i = 0; i < files.length; i++) {
      const reader = new FileReader();
      const file = files[i];
      reader.onloadstart = () => {
        setStartLoadingCounter(true);
        setCurrentFilename(file.name);
      };
      reader.onloadend = () => {
        if (i === files.length - 1) {
          setStartLoadingCounter(false);
        }
        setIsLoading(false);
      };
      reader.onload = () => {
        if (onValueChange) {
          onValueChange(uploadedFiles.concat(file.name));
        } else {
          setInternalValue(prev => prev.concat(file.name));
        }
        onUpload({
          file: reader.result,
          name: file.name
        });
        setIsLoading(false);
      };
      reader.readAsArrayBuffer(file);
    }
  };
  const handleDrop = e => {
    e.preventDefault();
    setIsHovered(false);
    if (!e.dataTransfer) {
      return null;
    }
    processFiles([...e.dataTransfer.files]);
  };
  const handleDragEnter = () => {
    setIsHovered(true);
  };
  const handleDragLeave = () => {
    setIsHovered(false);
  };
  const handleBrowseClick = () => {
    internalInputRef.current?.click();
  };
  const handleDismiss = filename => {
    if (internalInputRef.current) {
      internalInputRef.current.value = '';
    }
    if (onValueChange) {
      onValueChange(uploadedFiles.filter(name => name !== filename));
    } else {
      setInternalValue(prev => prev.filter(name => name !== filename));
    }
    if (onDismiss) {
      onDismiss(filename);
    }
  };
  const handleInputChange = e => {
    if (!e.target.files) {
      return null;
    }
    processFiles([...e.target.files]);
  };
  const getErrorMessage = () => {
    return errorMessage ?? error?.message;
  };
  const getDescription = () => {
    if (isHovered) {
      return /*#__PURE__*/React.createElement(Typography, {
        variant: "ui",
        size: "large",
        weight: "bold"
      }, "Drop file to upload.");
    }
    if (uploadedFiles.length > 0 && !isHovered && !(error || hasError) && !isLoading) {
      return /*#__PURE__*/React.createElement(S.DescriptionContainer, null, /*#__PURE__*/React.createElement(Button, {
        onClick: handleBrowseClick,
        loading: isLoading
      }, "Browse"), /*#__PURE__*/React.createElement(Typography, {
        variant: "ui",
        size: "large",
        weight: "bold"
      }, uploadedFiles.length, uploadedFiles.length > 1 ? ' files' : ' file', " added"), /*#__PURE__*/React.createElement(CheckCircleIcon, {
        variant: "bold",
        size: "large",
        fill: "#007A00"
      }));
    }
    if (error) {
      return /*#__PURE__*/React.createElement(S.DescriptionContainer, null, /*#__PURE__*/React.createElement(Button, {
        onClick: handleBrowseClick
      }, "Browse"), /*#__PURE__*/React.createElement(Typography, {
        variant: "ui",
        size: "large"
      }, /*#__PURE__*/React.createElement("b", null, currentFilename === '' ? 'File' : currentFilename), " could not be uploaded"));
    }
    if (isLoading) {
      return /*#__PURE__*/React.createElement(S.DescriptionContainer, null, /*#__PURE__*/React.createElement(Button, {
        onClick: handleBrowseClick,
        loading: true
      }, "Browse"), /*#__PURE__*/React.createElement(Typography, {
        variant: "ui",
        size: "large"
      }, /*#__PURE__*/React.createElement("b", null, multiple && uploadedFiles.length > 1 ? 'Multiple files' : currentFilename), ' ', "uploading..."));
    }
    return /*#__PURE__*/React.createElement(S.DescriptionContainer, null, /*#__PURE__*/React.createElement(Button, {
      onClick: handleBrowseClick,
      loading: isLoading
    }, "Browse"), /*#__PURE__*/React.createElement(Typography, {
      variant: "ui",
      size: "large",
      weight: "bold"
    }, multiple ? 'or drag files here' : 'or drag file here'));
  };
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(S.FileInputContainer, {
    "data-testid": "dropzone",
    $alignment: alignment,
    $isHovered: isHovered,
    $hasError: hasError || !!error,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDragOver: e => e.preventDefault(),
    onDrop: handleDrop
  }, /*#__PURE__*/React.createElement(S.FileInputInput, {
    ref: assignSharedRefs,
    id: id,
    type: "file",
    accept: accept.join(','),
    maxLength: maxSize,
    minLength: minSize,
    max: maxFiles,
    onChange: handleInputChange
  }), getDescription(), /*#__PURE__*/React.createElement(S.InfoText, {
    variant: "ui",
    size: "small"
  }, infoMessage)), error || hasError ? /*#__PURE__*/React.createElement(S.ErrorMessageContainer, null, /*#__PURE__*/React.createElement(ExclamationOctagonIcon, {
    variant: "regular",
    size: "medium",
    fill: "#D90606"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "ui",
    size: "small"
  }, getErrorMessage())) : null, uploadedFiles.length > 0 ? /*#__PURE__*/React.createElement(S.FilesListArea, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "ui",
    size: "medium"
  }, "Uploaded files:"), /*#__PURE__*/React.createElement(S.FilesTagsArea, null, uploadedFiles.map((filename, i) => /*#__PURE__*/React.createElement(Tag, {
    size: "medium",
    ariaLabel: filename,
    key: `${filename}-${i}`,
    contentText: filename,
    dismissable: true,
    readOnly: true,
    onDismiss: () => handleDismiss(filename)
  })))) : null);
});
export { FileInput };